// Set göstergesi sağ paneli (mockup 08 drawer + spec Rev 2 alanları): etiket override, birim
// (birim varyantı DR1 — müşteri genelinde, ayrı eylem: IndicatorUnitSection),
// değişim kolonu + artış yönü, anomali eşiği, dipnot, müşteri erişimi, bağlı form sorusu kodu,
// formül varyantları (FormulaEditor — set kodlarına karşı canlı doğrulama). Taslak satırı
// üst bileşende tutulur; burada yalnız `onChange(patch)` ile güncellenir (isDirty bar).

import {
  KPI_AGGREGATIONS,
  KPI_AGGREGATION_LABELS,
  KPI_CLIENT_ACCESS,
  KPI_CLIENT_ACCESS_LABELS,
  type KpiAggregation,
  type KpiClientAccess,
  type KpiMetric,
  type KpiSetIndicator,
} from "@mahir/shared";
import { type KpiPromoteResult } from "../../api/kpi";
import { useState } from "react";
import { type SearchSelectOption } from "../common/SearchSelect";
import { AggregationHint } from "./AggregationHint";
import { AggregationWeightField } from "./AggregationWeightField";
import { FormulaEditor, type FormulaServerError } from "./FormulaEditor";
import { IndicatorBreakdownSection } from "./IndicatorBreakdownSection";
import { IndicatorSourceRow } from "./IndicatorSourceRow";
import {
  IndicatorUnitSection,
  type CatalogUnitInfo,
  type IndicatorUnitActions,
  type IndicatorUnitApplied,
} from "./IndicatorUnitSection";
import { KpiModal } from "./KpiModal";
import { PromoteDialog } from "./PromoteDialog";
import { KPI_COPY } from "../../lib/kpi-ui";
import { memberParentUnit, type SetBreakdownView } from "../../lib/kpi-breakdown-set";
import { type WavgChild } from "../../lib/kpi-wavg-pairs";
import {
  KPI_INCREASE_OPTIONS,
  btnDanger,
  btnGhost,
  errorBox,
  fieldStyle,
  increaseToOption,
  inputStyle,
  labelStyle,
  monoStyle,
  optionToIncrease,
} from "./kpi-form-styles";

export interface IndicatorModalProps extends IndicatorUnitActions {
  indicator: KpiSetIndicator;
  /** birim müşteri genelinde (DR1) — kapsam satırı + unit-change gövdesi */
  clientId: string | null;
  clientName: string;
  /** katalog varsayılan birimi + alternatifleri (yüklenmediyse null) */
  catalogUnit: CatalogUnitInfo | null;
  /** birim değişimi uygulandı — üst bileşen başarı bandı + yerel güncelleme (DR6) */
  onUnitApplied: (r: IndicatorUnitApplied) => void;
  isCategory: boolean;
  setCodes: ReadonlySet<string>;
  /** kod → birim (override ?? metrik) — FormulaEditor CEVIR seçici */
  setUnits?: ReadonlyMap<string, string | null>;
  /** kod → etiket (override ?? metrik) — yazarken kod önerisi */
  setLabels?: ReadonlyMap<string, string>;
  /** TODO-KPI-91: kod → alt kalemler (set sırası) — AGIRLIKLI_ORT eşleme önizlemesi */
  setChildren?: ReadonlyMap<string, readonly WavgChild[]>;
  /** TODO-KPI-32: ağırlık adayları (set göstergeleri, değer = metrik id) */
  weightOptions?: readonly SearchSelectOption[];
  /** Kırılım (spec §6.3 DR3): set ağacı görünümü — toplamda "Kırılım" bölümü, üyede birim notu */
  breakdownView?: SetBreakdownView<KpiSetIndicator> | undefined;
  onEditBreakdownInCatalog?: (() => void) | undefined;
  onAddBreakdownGroup?: (() => void) | undefined;
  /** Sektör paketleri: katalog (ortak + müşteri) — kapsam satırı + Kataloğa yükselt (verilmezse yok) */
  catalog?: readonly KpiMetric[] | null | undefined;
  /** sayfada kaydedilmemiş set değişikliği — yükseltme kilitli (yeniden yükleme taslağı siler) */
  promoteLocked?: boolean | undefined;
  /** yükseltme bitti — üst bileşen seti/kataloğu yeniden okur */
  onPromoted?: ((r: KpiPromoteResult) => void) | undefined;
  canEdit: boolean;
  serverError: FormulaServerError | null;
  onChange: (patch: Partial<KpiSetIndicator>) => void;
  onRemove: () => void;
  onClose: () => void;
}

export function IndicatorModal(props: IndicatorModalProps): JSX.Element {
  const {
    indicator: ind,
    isCategory,
    setCodes,
    setUnits,
    setLabels,
    setChildren,
    weightOptions = [],
    canEdit,
    serverError,
    onChange,
    onRemove,
    onClose,
  } = props;
  const dis = !canEdit;
  const [unitPending, setUnitPending] = useState(false);
  // güvenlik/UX taraması 2026-10-08 (U-M2): değişiklikler ağaç taslağına anında yazılır (yerel taslak
  // yok) — açılıştaki gösterge anlık görüntüsüyle karşılaştırılır; fark varsa Esc/✕/arka plan onay ister.
  // Kataloğa yükselt kodu değiştirir (set yeniden okunur) — anlık görüntü yeni kodla tazelenir
  const [snap, setSnap] = useState(() => ({ code: ind.code, json: JSON.stringify(ind) }));
  if (snap.code !== ind.code) setSnap({ code: ind.code, json: JSON.stringify(ind) });
  const dirty = canEdit && (unitPending || JSON.stringify(ind) !== snap.json);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoted, setPromoted] = useState<{ packageIds: string[]; count: number } | null>(null);
  const metric = props.catalog?.find((m) => m.id === ind.metricId);

  const twoCol = !isCategory && ind.isDerived;
  const bv = props.breakdownView;
  const isTotal = bv?.kindOf(ind.id) === "total";
  const memberGroup = bv?.groupOf(ind.id) ?? null;
  const memberTotal = memberGroup && bv ? bv.totalOf(ind.id) : null;
  // TODO-KPI-32: override yokken etkin ağırlık = katalog varsayılanı (açılıştaki değer)
  const [catalogWeight] = useState(() =>
    ind.aggregationWeightOverride === null ? ind.aggregationWeightMetricId : null,
  );
  const weighted = (ind.aggregationOverride ?? ind.aggregation) === "weighted_avg";
  const catalogWeightLabel = weightOptions.find((o) => o.value === catalogWeight)?.label;
  const footer = (
    <>
      {canEdit ? (
        <>
          <button type="button" style={{ ...btnDanger, marginRight: 8 }} onClick={onRemove}>
            {isCategory ? "Kategoriyi veri setinden kaldır" : "Veri setinden kaldır"}
          </button>
          <span className="text-xs" style={{ color: "var(--color-text-3)", marginRight: "auto" }}>
            Yalnız üyelik silinir, değerler havuzda kalır. Değişiklikler ağaç taslağına yazılır —
            üstteki Kaydet ile kalıcı olur.
          </span>
        </>
      ) : null}
      <button type="button" style={btnGhost} onClick={onClose}>
        Kapat
      </button>
    </>
  );

  return (
    <>
      <KpiModal
        open
        kicker={isCategory ? "Kategori" : ind.isDerived ? "Gösterge · ƒx hesaplanan" : "Gösterge"}
        title={ind.label}
        description={`${ind.code}${ind.unit ? ` · ${ind.unit}` : ""} · ${KPI_COPY.unit.modalScope}; katalog tanımı Katalog ekranından değiştirilir.`}
        width={twoCol ? 1060 : 560}
        onClose={onClose}
        dirty={dirty}
        footer={footer}
      >
        <div
          className="grid gap-x-6"
          style={{
            gridTemplateColumns: twoCol ? "repeat(auto-fit, minmax(380px, 1fr))" : "1fr",
            alignItems: "start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            {props.catalog ? (
              <IndicatorSourceRow
                metric={metric}
                sourcePackageId={ind.sourcePackageId}
                promoted={promoted}
                canPromote={canEdit && props.onPromoted !== undefined}
                locked={props.promoteLocked === true || dirty}
                onPromote={() => setPromoteOpen(true)}
              />
            ) : null}
            {serverError && !serverError.formulaId && !serverError.cyclePath ? (
              <div style={{ ...errorBox, marginBottom: 12 }} role="alert">
                {serverError.message}
              </div>
            ) : null}

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="ind-label">
                Etiket (override)
              </label>
              <input
                id="ind-label"
                style={inputStyle}
                value={ind.labelOverride ?? ""}
                placeholder={ind.label}
                disabled={dis}
                onChange={(e) =>
                  onChange({ labelOverride: e.target.value === "" ? null : e.target.value })
                }
              />
            </div>

            {memberGroup && bv ? (
              <div
                style={{ ...fieldStyle, color: "var(--color-text-3)", fontSize: "0.875rem" }}
                data-testid="kpi-member-unit-note"
              >
                <span>
                  {KPI_COPY.breakdown.calcGroup.memberInfo(
                    memberTotal?.labelOverride ?? memberTotal?.label ?? "—",
                    memberGroup.label,
                  )}
                </span>
                <span>
                  {KPI_COPY.breakdown.calcGroup.footerLead}{" "}
                  <strong>
                    {KPI_COPY.breakdown.calcGroup.footerInherit(memberParentUnit(ind, bv) ?? "")}
                  </strong>
                  .
                </span>
              </div>
            ) : !isCategory ? (
              <IndicatorUnitSection
                indicator={ind}
                clientId={props.clientId}
                clientName={props.clientName}
                catalogUnit={props.catalogUnit}
                {...(setUnits ? { setUnits } : {})}
                disabled={dis}
                onApplied={props.onUnitApplied}
                onPendingChange={setUnitPending}
                {...(props.onOpenFormula ? { onOpenFormula: props.onOpenFormula } : {})}
                {...(props.onInspectValues ? { onInspectValues: props.onInspectValues } : {})}
                {...(props.onInspectReported ? { onInspectReported: props.onInspectReported } : {})}
                {...(props.onInspectTargets ? { onInspectTargets: props.onInspectTargets } : {})}
              />
            ) : null}

            {isTotal && bv ? (
              <IndicatorBreakdownSection
                indicator={ind}
                view={bv}
                disabled={dis}
                onChange={(id) => onChange({ calculationGroupMetricId: id })}
                onEditCatalog={props.onEditBreakdownInCatalog}
                onAddGroup={canEdit ? props.onAddBreakdownGroup : undefined}
              />
            ) : null}

            {!isCategory ? (
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="ind-agg">
                  Konsolide (grup müşteri)
                </label>
                <select
                  id="ind-agg"
                  style={inputStyle}
                  value={ind.aggregationOverride ?? ""}
                  disabled={dis}
                  onChange={(e) =>
                    onChange({
                      aggregationOverride:
                        e.target.value === "" ? null : (e.target.value as KpiAggregation),
                    })
                  }
                >
                  <option value="">
                    Katalog varsayılanı
                    {ind.aggregationOverride === null
                      ? ` (${KPI_AGGREGATION_LABELS[ind.aggregation]})`
                      : ""}
                  </option>
                  {KPI_AGGREGATIONS.map((a) => (
                    <option key={a} value={a}>
                      {KPI_AGGREGATION_LABELS[a]}
                    </option>
                  ))}
                </select>
                <AggregationHint
                  value={ind.aggregationOverride ?? ind.aggregation}
                  isDerived={ind.isDerived}
                  inherited={ind.aggregationOverride === null}
                />
              </div>
            ) : null}
            {!isCategory && weighted ? (
              <AggregationWeightField
                id="ind-agg-weight"
                value={ind.aggregationWeightOverride ?? ""}
                options={weightOptions.filter((o) => o.value !== ind.metricId)}
                emptyLabel={`${KPI_COPY.aggregationWeight.catalogDefault}${catalogWeightLabel ? ` (${catalogWeightLabel})` : ""}`}
                missing={!ind.aggregationWeightOverride && !catalogWeight}
                disabled={dis}
                onChange={(v) => onChange({ aggregationWeightOverride: v || null })}
              />
            ) : null}

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="ind-access">
                Müşteri erişimi
              </label>
              <select
                id="ind-access"
                style={inputStyle}
                value={ind.clientAccess}
                disabled={dis}
                onChange={(e) => onChange({ clientAccess: e.target.value as KpiClientAccess })}
              >
                {KPI_CLIENT_ACCESS.map((a) => (
                  <option key={a} value={a}>
                    {KPI_CLIENT_ACCESS_LABELS[a]}
                  </option>
                ))}
              </select>
              <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                Etkin: {KPI_CLIENT_ACCESS_LABELS[ind.effectiveClientAccess]}
                {ind.clientAccess === "inherit" ? " (üstten)" : ""}
              </div>
            </div>

            {!isCategory ? (
              <>
                <div style={fieldStyle}>
                  <span style={labelStyle}>Değişim kolonu</span>
                  <label
                    className="flex items-center gap-2 text-sm"
                    style={{ color: "var(--color-text)", minHeight: 32 }}
                  >
                    <input
                      type="checkbox"
                      checked={ind.showChange}
                      disabled={dis}
                      onChange={(e) => onChange({ showChange: e.target.checked })}
                    />
                    Raporda ve ekranda göster
                  </label>
                  <div
                    role="radiogroup"
                    aria-label="Artış yönü"
                    className="flex items-center gap-1 text-sm flex-wrap"
                  >
                    <span style={{ color: "var(--color-text-3)", marginRight: 4 }}>Artış:</span>
                    {KPI_INCREASE_OPTIONS.map((o) => {
                      const on = increaseToOption(ind.increaseIsGood) === o.value;
                      return (
                        <button
                          key={o.value}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          disabled={dis}
                          onClick={() => onChange({ increaseIsGood: optionToIncrease(o.value) })}
                          style={{
                            ...btnGhost,
                            minHeight: 32,
                            padding: "4px 10px",
                            fontSize: "0.8rem",
                            ...(on
                              ? {
                                  background: "var(--color-primary)",
                                  color: "var(--color-surface)",
                                  borderColor: "var(--color-primary)",
                                }
                              : {}),
                          }}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                    Özet ekranındaki kartlar bu işaretli göstergelerden, ağaçtaki sırayla oluşur.
                  </div>
                </div>

                <div style={fieldStyle}>
                  <label style={labelStyle} htmlFor="ind-anomaly">
                    Anomali eşiği (% değişim)
                  </label>
                  <input
                    id="ind-anomaly"
                    type="number"
                    step="any"
                    style={{ ...inputStyle, maxWidth: 140 }}
                    value={ind.anomalyThreshold ?? ""}
                    disabled={dis}
                    placeholder="örn. 30"
                    onChange={(e) =>
                      onChange({
                        anomalyThreshold: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div style={fieldStyle}>
                  <label style={labelStyle} htmlFor="ind-question">
                    Bağlı form sorusu kodu
                  </label>
                  <input
                    id="ind-question"
                    style={{ ...inputStyle, ...monoStyle }}
                    value={ind.linkedQuestionCode ?? ""}
                    maxLength={64}
                    disabled={dis}
                    placeholder="örn. cevre.enerji_toplam"
                    onChange={(e) =>
                      onChange({
                        linkedQuestionCode: e.target.value === "" ? null : e.target.value,
                      })
                    }
                  />
                </div>
              </>
            ) : null}

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="ind-footnote">
                Rapor dipnotu
              </label>
              <textarea
                id="ind-footnote"
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
                value={ind.footnote ?? ""}
                maxLength={2000}
                disabled={dis}
                onChange={(e) =>
                  onChange({ footnote: e.target.value === "" ? null : e.target.value })
                }
              />
            </div>
          </div>
          {twoCol ? (
            <div
              className="rounded-lg p-3"
              style={{
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                minWidth: 0,
              }}
            >
              {ind.isDerived ? (
                <FormulaEditor
                  formulas={ind.formulas}
                  activeFormulaId={ind.activeFormulaId}
                  codes={setCodes}
                  {...(setUnits ? { unitsByCode: setUnits } : {})}
                  {...(setLabels ? { labelsByCode: setLabels } : {})}
                  {...(setChildren ? { childrenByCode: setChildren } : {})}
                  ownCode={ind.code}
                  {...(isTotal && bv
                    ? {
                        ownDimensions: bv
                          .groupsOf(ind.id)
                          .flatMap((g) =>
                            g.breakdown ? [{ slug: g.breakdown.dimension, label: g.label }] : [],
                          ),
                      }
                    : {})}
                  scope="set"
                  disabled={dis}
                  serverError={serverError}
                  onChange={(formulas, activeFormulaId) => onChange({ formulas, activeFormulaId })}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </KpiModal>
      {promoteOpen && metric ? (
        // kardeş pencere (iç içe değil): alttakinin odak tuzağı karışmaz, Esc'i KpiModal ayırır
        <PromoteDialog
          open
          metric={metric}
          catalog={props.catalog ?? []}
          onClose={() => setPromoteOpen(false)}
          onPromoted={(r, packageIds) => {
            setPromoteOpen(false);
            setPromoted({ packageIds, count: r.promoted.length });
            props.onPromoted?.(r);
          }}
        />
      ) : null}
    </>
  );
}
